import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiFileText, FiUpload, FiDownload, FiCheckCircle, FiClock, FiUsers, FiSearch, FiChevronRight, FiActivity, FiX, FiCheck } from 'react-icons/fi';
import { fetchAPI } from '../../utils/api';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';

export default function AdminExams() {
    const [exams, setExams] = useState([]);
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmissionsModalOpen, setIsSubmissionsModalOpen] = useState(false);
    const [currentExam, setCurrentExam] = useState(null);
    const [submissions, setSubmissions] = useState([]);
    const [submissionsLoading, setSubmissionsLoading] = useState(false);
    const [selectedExamForSubmissions, setSelectedExamForSubmissions] = useState(null);

    const [formData, setFormData] = useState({
        class_id: '',
        name: '',
        description: '',
        exam_date: new Date().toISOString().split('T')[0],
        end_date: '',
        paper_url: '',
        answer_key_url: '',
        is_active: 1
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [examsData, classesData] = await Promise.all([
                fetchAPI('/admin/get_exams.php'),
                fetchAPI('/admin/get_classes.php')
            ]);
            setExams(examsData);
            setClasses(classesData);
        } catch (err) {
            toast.error("Failed to load exams data");
        } finally {
            setLoading(false);
        }
    };

    const handleSaveExam = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await fetchAPI('/admin/save_exam.php', {
                method: 'POST',
                body: JSON.stringify(currentExam ? { ...formData, id: currentExam.id } : formData)
            });
            toast.success(currentExam ? "Exam updated" : "Exam created");
            setIsModalOpen(false);
            loadData();
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e, field) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadFormData = new FormData();
        uploadFormData.append('resource_file', file);

        toast.loading(`Uploading ${field.replace('_', ' ')}...`, { id: 'adminUpload' });
        try {
            const result = await fetchAPI('/admin/upload_exam_resource.php', {
                method: 'POST',
                body: uploadFormData
            });

            setFormData(prev => ({ ...prev, [field]: result.url }));
            toast.success("File uploaded", { id: 'adminUpload' });
        } catch (err) {
            toast.error(err.message, { id: 'adminUpload' });
        }
    };

    const handleDeleteExam = async (id) => {
        if (!window.confirm("Are you sure you want to delete this exam?")) return;
        try {
            await fetchAPI('/admin/delete_exam.php', {
                method: 'POST',
                body: JSON.stringify({ id })
            });
            toast.success("Exam deleted");
            loadData();
        } catch (err) {
            toast.error(err.message);
        }
    };

    const openEditModal = (exam) => {
        setCurrentExam(exam);
        setFormData({
            class_id: exam.class_id,
            name: exam.name,
            description: exam.description || '',
            exam_date: exam.exam_date,
            end_date: exam.end_date || '',
            paper_url: exam.paper_url || '',
            answer_key_url: exam.answer_key_url || '',
            is_active: parseInt(exam.is_active)
        });
        setIsModalOpen(true);
    };

    const openSubmissionsModal = async (exam) => {
        setSelectedExamForSubmissions(exam);
        setIsSubmissionsModalOpen(true);
        setSubmissionsLoading(true);
        try {
            const data = await fetchAPI(`/admin/get_exam_submissions.php?id=${exam.id}`);
            setSubmissions(data);
        } catch (err) {
            toast.error("Failed to load submissions");
        } finally {
            setSubmissionsLoading(false);
        }
    };

    const handleUpdateSubmission = async (subId, update) => {
        try {
            await fetchAPI('/admin/update_exam_submission.php', {
                method: 'POST',
                body: JSON.stringify({ id: subId, ...update })
            });
            toast.success("Submission updated");
            // Refresh submissions
            const data = await fetchAPI(`/admin/get_exam_submissions.php?id=${selectedExamForSubmissions.id}`);
            setSubmissions(data);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleReviewUpload = async (e, subId) => {
        const file = e.target.files[0];
        if (!file) return;

        const uploadFormData = new FormData();
        uploadFormData.append('resource_file', file);

        const uploadToast = toast.loading("Uploading Review PDF...");
        try {
            const result = await fetchAPI('/admin/upload_exam_resource.php', {
                method: 'POST',
                body: uploadFormData
            });

            await handleUpdateSubmission(subId, { review_url: result.url });
            toast.success("Review uploaded successfully", { id: uploadToast });
        } catch (err) {
            toast.error(err.message, { id: uploadToast });
        }
    };

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-20">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 font-black uppercase tracking-widest text-[10px]">Syncing Exams Database...</p>
        </div>
    );

    return (
        <div className="p-6 md:p-8 space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white p-8 rounded-[2.5rem] shadow-xl border border-gray-100 relative overflow-hidden">
                <div className="absolute top-0 right-0 p-8 opacity-5">
                    <FiActivity size={120} className="text-blue-600" />
                </div>
                <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-black uppercase tracking-widest mb-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse"></div>
                        Exam Management Center
                    </div>
                    <h1 className="text-4xl font-black text-gray-900 tracking-tighter uppercase italic leading-none">
                        Examination <span className="text-blue-600">Protocol</span>
                    </h1>
                </div>
                <button
                    onClick={() => {
                        setCurrentExam(null);
                        setFormData({
                            class_id: '', name: '', description: '',
                            exam_date: new Date().toISOString().split('T')[0],
                            end_date: '', paper_url: '', answer_key_url: '', is_active: 1
                        });
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-3 px-8 py-4 bg-gray-900 text-white rounded-2xl font-black uppercase tracking-widest text-xs hover:bg-blue-600 transition-all shadow-xl shadow-gray-200 hover:shadow-blue-200"
                >
                    <FiPlus size={18} /> Initiate New Exam
                </button>
            </div>

            {/* Exams Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {exams.map((exam) => (
                    <div key={exam.id} className="group bg-white rounded-[2rem] border border-gray-100 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden flex flex-col">
                        <div className="p-6 sm:p-8 flex-1">
                            <div className="flex items-start justify-between mb-6">
                                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white shadow-lg ${exam.is_active == 1 ? 'bg-blue-600 shadow-blue-100' : 'bg-gray-400 shadow-gray-100'}`}>
                                    <FiFileText size={24} />
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => openEditModal(exam)} className="p-3 bg-gray-50 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all">
                                        <FiEdit2 size={16} />
                                    </button>
                                    <button onClick={() => handleDeleteExam(exam.id)} className="p-3 bg-gray-50 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all">
                                        <FiTrash2 size={16} />
                                    </button>
                                </div>
                            </div>

                            <div className="mb-6">
                                <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em] mb-1">
                                    {classes.find(c => c.id == exam.class_id)?.class_name || 'Class ID: ' + exam.class_id}
                                </p>
                                <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight line-clamp-1">{exam.name}</h3>
                                {exam.description && <p className="text-xs text-gray-500 mt-2 line-clamp-2 leading-relaxed">{exam.description}</p>}
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-6">
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                        <FiClock size={10} /> Release
                                    </p>
                                    <p className="text-[11px] font-bold text-gray-900">{exam.exam_date}</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-2xl">
                                    <p className="text-[8px] font-black text-gray-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                                        <FiClock size={10} /> Deadline
                                    </p>
                                    <p className="text-[11px] font-bold text-gray-900">{exam.end_date || 'No Limit'}</p>
                                </div>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-8">
                                {exam.paper_url ? (
                                    <a href={exam.paper_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-green-50 text-green-600 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                        <FiCheckCircle size={12} /> Paper Uploaded
                                    </a>
                                ) : (
                                    <span className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                        <FiX size={12} /> Paper Missing
                                    </span>
                                )}
                                {exam.answer_key_url && (
                                    <a href={exam.answer_key_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2">
                                        <FiCheckCircle size={12} /> Key Uploaded
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 border-t border-gray-100 group-hover:bg-blue-600 transition-colors duration-500">
                            <button
                                onClick={() => openSubmissionsModal(exam)}
                                className="w-full py-4 bg-white text-gray-900 rounded-2xl font-black uppercase tracking-widest text-[10px] flex items-center justify-center gap-3 shadow-lg group-hover:bg-gray-900 group-hover:text-white transition-all duration-500"
                            >
                                <FiUsers size={16} /> View Submissions <FiChevronRight />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* Create/Edit Modal */}
            <AnimatePresence>
                {isModalOpen && (
                    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6">
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setIsModalOpen(false)}
                            className="absolute inset-0 bg-gray-900/60 backdrop-blur-md"
                        />
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative w-full max-w-2xl bg-white rounded-[2.5rem] shadow-2xl overflow-hidden"
                        >
                            <div className="p-8 md:p-12 overflow-y-auto max-h-[90vh]">
                                <h2 className="text-3xl font-black text-gray-900 italic uppercase tracking-tighter mb-8">
                                    {currentExam ? 'Update' : 'Initialize'} <span className="text-blue-600">Exam Protocol</span>
                                </h2>

                                <form onSubmit={handleSaveExam} className="space-y-6">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Target Class</label>
                                            <select
                                                required
                                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900"
                                                value={formData.class_id}
                                                onChange={(e) => setFormData({ ...formData, class_id: e.target.value })}
                                            >
                                                <option value="">Select Class</option>
                                                {classes.map(c => <option key={c.id} value={c.id}>{c.class_name}</option>)}
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Exam Name</label>
                                            <input
                                                required
                                                type="text"
                                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900"
                                                placeholder="e.g. Unit 05 - Integration"
                                                value={formData.name}
                                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Detailed Instructions</label>
                                        <textarea
                                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900 h-32"
                                            placeholder="Specify rules, time limit, and marking criteria..."
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Release Date</label>
                                            <input
                                                required
                                                type="date"
                                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900"
                                                value={formData.exam_date}
                                                onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Deadline (Optional)</label>
                                            <input
                                                type="datetime-local"
                                                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-gray-900"
                                                value={formData.end_date}
                                                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Question Paper (PDF/Image)</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    className="flex-1 px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-[10px] text-gray-900"
                                                    placeholder="URL or Upload ->"
                                                    value={formData.paper_url}
                                                    onChange={(e) => setFormData({ ...formData, paper_url: e.target.value })}
                                                />
                                                <div className="relative">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,image/*"
                                                        className="hidden"
                                                        id="adminPaperUpload"
                                                        onChange={(e) => handleFileUpload(e, 'paper_url')}
                                                    />
                                                    <label
                                                        htmlFor="adminPaperUpload"
                                                        className="h-full px-5 flex items-center justify-center bg-gray-900 text-white rounded-2xl cursor-pointer hover:bg-blue-600 transition-all"
                                                    >
                                                        <FiUpload size={14} />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="space-y-2">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Answer Key (Optional)</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    className="flex-1 px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-2 focus:ring-blue-500 outline-none font-bold text-[10px] text-gray-900"
                                                    placeholder="URL or Upload ->"
                                                    value={formData.answer_key_url}
                                                    onChange={(e) => setFormData({ ...formData, answer_key_url: e.target.value })}
                                                />
                                                <div className="relative">
                                                    <input
                                                        type="file"
                                                        accept=".pdf,image/*"
                                                        className="hidden"
                                                        id="adminKeyUpload"
                                                        onChange={(e) => handleFileUpload(e, 'answer_key_url')}
                                                    />
                                                    <label
                                                        htmlFor="adminKeyUpload"
                                                        className="h-full px-5 flex items-center justify-center bg-gray-900 text-white rounded-2xl cursor-pointer hover:bg-blue-600 transition-all"
                                                    >
                                                        <FiUpload size={14} />
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex gap-4 pt-6">
                                        <button
                                            type="button"
                                            onClick={() => setIsModalOpen(false)}
                                            className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-gray-200 transition-all"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="flex-[1.5] py-4 bg-blue-600 text-white rounded-2xl font-black uppercase tracking-widest text-[10px] hover:bg-blue-700 transition-all shadow-xl shadow-blue-200"
                                        >
                                            {currentExam ? 'Save Changes' : 'Launch Exam'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Submissions Modal */}
            <AnimatePresence>
                {isSubmissionsModalOpen && (
                    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-6">
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                            onClick={() => setIsSubmissionsModalOpen(false)}
                            className="absolute inset-0 bg-gray-900/80 backdrop-blur-xl"
                        />
                        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            className="relative w-full max-w-5xl bg-white rounded-[3rem] shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                        >
                            <div className="p-8 md:p-12 pb-6 border-b border-gray-100 flex items-center justify-between">
                                <div>
                                    <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.3em] mb-1">Submissions Hub</p>
                                    <h2 className="text-3xl font-black text-gray-900 uppercase italic tracking-tighter">
                                        {selectedExamForSubmissions?.name}
                                    </h2>
                                </div>
                                <button onClick={() => setIsSubmissionsModalOpen(false)} className="w-12 h-12 rounded-2xl bg-gray-50 text-gray-400 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-all">
                                    <FiX size={24} />
                                </button>
                            </div>

                            <div className="flex-1 overflow-y-auto p-8 md:p-12 space-y-4">
                                {submissionsLoading ? (
                                    <div className="py-20 flex flex-col items-center">
                                        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Aggregating Student Work...</p>
                                    </div>
                                ) : submissions.length === 0 ? (
                                    <div className="py-20 text-center border-2 border-dashed border-gray-100 rounded-[2.5rem]">
                                        <FiUsers size={48} className="mx-auto text-gray-200 mb-4" />
                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">No submissions detected yet</p>
                                    </div>
                                ) : (
                                    submissions.map((sub) => (
                                        <div key={sub.id} className="p-6 bg-gray-50 rounded-[2rem] border border-gray-100 flex flex-col md:flex-row md:items-center justify-between gap-6 hover:bg-white hover:shadow-xl hover:border-blue-100 transition-all duration-300">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 rounded-xl bg-white text-blue-600 flex items-center justify-center shadow-sm font-black text-lg uppercase italic">
                                                    {sub.full_name.charAt(0)}
                                                </div>
                                                <div>
                                                    <h4 className="font-black text-gray-900 uppercase tracking-tight text-sm">{sub.full_name}</h4>
                                                    <p className="text-[10px] text-gray-400 font-bold">{sub.email} • {sub.submitted_at}</p>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-4">
                                                <div className="flex flex-wrap gap-2">
                                                    {sub.submission_url.split(',').map((url, idx) => (
                                                        <a
                                                            key={idx}
                                                            href={url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="flex items-center gap-2 px-4 py-3 bg-white text-gray-900 rounded-xl text-[9px] font-black uppercase tracking-widest shadow-sm hover:bg-gray-900 hover:text-white transition-all border border-gray-100"
                                                        >
                                                            <FiDownload /> Part {idx + 1}
                                                        </a>
                                                    ))}

                                                    {sub.review_url ? (
                                                        <a
                                                            href={sub.review_url}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest shadow-lg shadow-blue-100 hover:bg-blue-700 transition-all border border-blue-500"
                                                        >
                                                            <FiFileText /> Review PDF
                                                        </a>
                                                    ) : (
                                                        <div className="relative">
                                                            <input
                                                                type="file"
                                                                accept=".pdf"
                                                                className="hidden"
                                                                id={`review-upload-${sub.id}`}
                                                                onChange={(e) => handleReviewUpload(e, sub.id)}
                                                            />
                                                            <label
                                                                htmlFor={`review-upload-${sub.id}`}
                                                                className="flex items-center gap-2 px-4 py-3 bg-orange-50 text-orange-600 rounded-lg text-[9px] font-black uppercase tracking-widest cursor-pointer hover:bg-orange-100 transition-all border border-orange-200 border-dashed"
                                                            >
                                                                <FiUpload /> Upload Review
                                                            </label>
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="number"
                                                        placeholder="Marks"
                                                        className="w-20 px-4 py-3 bg-white border border-gray-200 rounded-xl text-center font-black text-xs outline-none focus:ring-2 focus:ring-blue-500"
                                                        defaultValue={sub.marks}
                                                        onBlur={(e) => handleUpdateSubmission(sub.id, { marks: e.target.value })}
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder="Add Feedback..."
                                                        className="flex-1 md:w-64 px-4 py-3 bg-white border border-gray-200 rounded-xl font-bold text-xs outline-none focus:ring-2 focus:ring-blue-500"
                                                        defaultValue={sub.feedback}
                                                        onBlur={(e) => handleUpdateSubmission(sub.id, { feedback: e.target.value })}
                                                    />
                                                </div>

                                                <div className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest ${sub.status === 'graded' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                                                    {sub.status}
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
}
